import { useFormContext, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Badge } from "@bcc/components/ui/badge";
import type { ProductFormData } from "../schemas/product-schema";

export type SectionId = "details" | "attributes" | "intensity" | "variants";

const FIELD_TO_SECTION: Record<string, SectionId> = {
  title: "details",
  description: "details",
  roasterNotes: "details",
  imageUploads: "details",
  mainFlavorCategory: "attributes",
  secondaryFlavorCategory: "attributes",
  isDecaf: "attributes",
  productType: "attributes",
  roastLevel: "attributes",
  process: "attributes",
  varietal: "attributes",
  tastingNotes: "attributes",
  brewMethod: "attributes",
  certifications: "attributes",
  badges: "attributes",
  character: "attributes",
  region: "attributes",
  country: "attributes",
  elevationStart: "attributes",
  elevationEnd: "attributes",
  intensityPosition: "intensity",
  intensityZone: "intensity",
  variants: "variants",
};

// field key → i18n key suffix under views.shopifyImportModal.productForm.labels.*
const PRODUCT_LABEL_KEYS: Record<string, string> = {
  title: "title",
  description: "description",
  roasterNotes: "roasterNotes",
  imageUploads: "imageUploads",
  mainFlavorCategory: "mainFlavorCategory",
  secondaryFlavorCategory: "secondaryFlavorCategory",
  isDecaf: "isDecaf",
  productType: "productType",
  roastLevel: "roastLevel",
  process: "process",
  varietal: "varietal",
  tastingNotes: "tastingNotes",
  brewMethod: "brewMethod",
  certifications: "certifications",
  badges: "badges",
  character: "character",
  region: "region",
  country: "country",
  elevationStart: "elevationStart",
  elevationEnd: "elevationEnd",
  intensityPosition: "intensityPosition",
  intensityZone: "intensityPosition",
};

// variant field key → i18n key suffix under components.ProductForm.*
const VARIANT_LABEL_KEYS: Record<string, string> = {
  bagSize: "bagSize",
  grind: "grind",
  price: "msrp",
  wholesalePrice: "wholesalePrice",
  term: "term",
  startDate: "startDate",
  endDate: "endDate",
  weeklyInventory: "weeklyInventory",
};

export interface ErrorPill {
  path: string;
  label: string;
  section: SectionId;
}

export function deriveErrorPills(errors: FieldErrors, t: TFunction): ErrorPill[] {
  const pills: ErrorPill[] = [];

  for (const [key, value] of Object.entries(errors)) {
    if (!value) continue;

    if (key === "variants" && Array.isArray(value)) {
      value.forEach((variantErrors, idx) => {
        if (!variantErrors || typeof variantErrors !== "object") return;
        for (const [fieldKey] of Object.entries(variantErrors as FieldErrors)) {
          const labelKey = VARIANT_LABEL_KEYS[fieldKey];
          const fieldLabel = labelKey ? t(`components.ProductForm.${labelKey}`) : fieldKey;
          const prefix = t("views.shopifyImportModal.productForm.errorSummary.variantPrefix", {
            index: idx + 1,
          });
          pills.push({
            path: `variants.${idx}.${fieldKey}`,
            label: `${String(prefix)}: ${String(fieldLabel)}`,
            section: "variants",
          });
        }
      });
    } else if (typeof value === "object" && "message" in value) {
      const labelKey = PRODUCT_LABEL_KEYS[key];
      pills.push({
        path: key,
        label: labelKey ? t(`views.shopifyImportModal.productForm.labels.${labelKey}`) : key,
        section: FIELD_TO_SECTION[key] ?? "details",
      });
    }
  }

  return pills;
}

interface ProductFormErrorPillsProps {
  isDirty: boolean;
  isSubmitted: boolean;
  onPillClick: (fieldPath: string, section: SectionId) => void;
}

export function ProductFormErrorPills({ isDirty, isSubmitted, onPillClick }: Readonly<ProductFormErrorPillsProps>) {
  const {
    formState: { errors },
  } = useFormContext<ProductFormData>();
  const { t } = useTranslation();

  if (!isDirty && !isSubmitted) return null;

  const pills = deriveErrorPills(errors, t);
  if (pills.length === 0) return null;

  return (
    <fieldset
      className="flex flex-wrap gap-1.5 mb-4 border-0 p-0 m-0"
      aria-label={t("views.shopifyImportModal.productForm.errorPills.label")}
    >
      {pills.map((pill) => (
        <Badge key={pill.path} variant="destructive" asChild testId={`error-pill-${pill.path}`}>
          <button type="button" onClick={() => onPillClick(pill.path, pill.section)}>
            {pill.label}
          </button>
        </Badge>
      ))}
    </fieldset>
  );
}
